import { Minus, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MultiSelectAutogrow } from "@/components/multi-select-autogrow";
import { normalizeId, normalizeText } from "@/lib/normalize";
import { createEmptyEntry, type EntryData } from "@/types/entry";
import type { MultiSelectItem } from "@/types/multi-select";

interface DailyEntryFormProps {
  dateKey: string;
  entry: EntryData;
  symptomOptions: string[];
  transportationOptions: string[];
  appointmentOptions: string[];
  weatherSummary: string | null;
  weatherLoading: boolean;
  weatherError: string | null;
  weatherLocationLabel: string | null;
  weatherSourceUrl: string | null;
  hasWeatherLocation: boolean;
  onUseCurrentLocation: () => void;
  onSetCity: () => void;
  onSave: (entry: EntryData) => void;
}

const defaultEntry = createEmptyEntry();

const chipSelectedClass =
  "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)] border-[var(--color-border-focus)]";
const chipDefaultClass =
  "bg-[var(--color-bg-input)] text-[var(--color-text-secondary)] border-transparent hover:bg-[var(--color-bg-hover)]";
const fieldIds = {
  symptoms: "symptoms",
  outings: "outings",
  transportation: "transportation",
  appointments: "appointments",
  notes: "daily-notes",
} as const;

const toItems = (values: string[]): MultiSelectItem[] => {
  return values.map(label => ({ id: normalizeId(label), label }));
};

const toUniqueLabels = (items: MultiSelectItem[]): string[] => {
  const seen = new Set<string>();
  const labels: string[] = [];

  for (const item of items) {
    const normalized = normalizeText(item.label);
    if (seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    labels.push(item.label.trim());
  }

  return labels.filter(label => label.length > 0);
};

const formatNumber = (value: number | null, suffix = ""): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return `${value}${suffix}`;
};

interface RangeNumberFieldProps {
  id: string;
  label: string;
  helperText: string;
  min: number;
  max: number;
  value: number | null;
  onChange: (next: number) => void;
}

function RangeNumberField({ id, label, helperText, min, max, value, onChange }: RangeNumberFieldProps) {
  const currentValue = value ?? min;
  const values = Array.from({ length: max - min + 1 }, (_, index) => min + index);
  const percent = ((currentValue - min) / (max - min)) * 100;

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="block text-lg text-[var(--color-text-primary)]">
        {label}
      </label>
      <p className="text-sm text-[var(--color-text-tertiary)]">{helperText}</p>
      <div className="relative pb-10 pt-2">
        <div className="relative mx-10">
          <div className="pointer-events-none absolute left-0 right-0 top-8 z-0 h-2 rounded-full bg-[var(--color-border-focus)]" />
          <input
            id={id}
            name={id}
            type="range"
            min={min}
            max={max}
            step={1}
            value={currentValue}
            onChange={event => onChange(Number.parseInt(event.target.value, 10))}
            className="value-range relative z-10 h-14 w-full"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-8 z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-[var(--color-border-focus)] bg-[var(--color-accent-muted)] text-xl text-[var(--color-text-primary)]"
            style={{ left: `${percent}%` }}
          >
            {currentValue}
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-x-10 flex justify-between -mx-2 text-sm text-[var(--color-text-tertiary)]"
          style={{ top: "3.3rem" }}
        >
          {values.map(scaleValue => (
            <span key={scaleValue} className="w-4 text-center">{scaleValue}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

interface CounterFieldProps {
  id: string;
  label: string;
  helperText: string;
  value: number | null;
  min?: number;
  max?: number;
  step?: number;
  inputMode?: "decimal" | "numeric";
  formatValue?: (value: number) => number;
  onChange: (next: number | null) => void;
}

const formatDecimal = (value: number): string => {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
};

function CounterField({
  id,
  label,
  helperText,
  value,
  min = 0,
  max = 99,
  step = 1,
  inputMode = "numeric",
  formatValue = value => Math.floor(value),
  onChange,
}: CounterFieldProps) {
  const [draftValue, setDraftValue] = useState(value === null ? "" : String(value));

  useEffect(() => {
    setDraftValue(value === null ? "" : String(value));
  }, [value]);

  const parseDraftValue = () => {
    const trimmed = draftValue.trim();
    if (trimmed.length === 0) {
      return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return value;
    }

    const clamped = Math.min(max, Math.max(min, parsed));
    return formatValue(clamped);
  };

  const commit = () => {
    const next = parseDraftValue() ?? 0;
    setDraftValue(formatDecimal(next));
    onChange(next);
  };

  const update = (next: number) => {
    const formatted = formatValue(Math.min(max, Math.max(min, next)));
    setDraftValue(formatDecimal(formatted));
    onChange(formatted);
  };

  const currentValue = parseDraftValue() ?? 0;

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="block text-lg text-[var(--color-text-primary)]">
        {label}
      </label>
      <p className="text-sm text-[var(--color-text-tertiary)]">{helperText}</p>
      <div className="grid grid-cols-[3rem_1fr_3rem] items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label.toLowerCase()}`}
          onClick={() => update(currentValue - step)}
          className="flex h-12 items-center justify-center rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
        >
          <Minus size={18} />
        </button>
        <input
          id={id}
          name={id}
          type="number"
          inputMode={inputMode}
          min={min}
          max={max}
          step={step}
          value={draftValue}
          onChange={event => setDraftValue(event.target.value)}
          onFocus={event => event.currentTarget.select()}
          onBlur={commit}
          className="h-12 w-full rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] px-4 text-center text-xl text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] focus:border-[var(--color-border-focus)]"
        />
        <button
          type="button"
          aria-label={`Increase ${label.toLowerCase()}`}
          onClick={() => update(currentValue + step)}
          className="flex h-12 items-center justify-center rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

interface DurationFieldProps {
  id: string;
  label: string;
  helperText: string;
  value: number | null;
  onChange: (next: number | null) => void;
}

const formatDuration = (hoursValue: number): string => {
  const totalMinutes = Math.round(Math.min(24, Math.max(0, hoursValue)) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours} hr ${minutes} min`;
};

const editDistance = (left: string, right: string): number => {
  const rows = left.length + 1;
  const cols = right.length + 1;
  const distances = Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));

  for (let row = 0; row < rows; row += 1) {
    distances[row]![0] = row;
  }
  for (let col = 0; col < cols; col += 1) {
    distances[0]![col] = col;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      distances[row]![col] = Math.min(
        distances[row - 1]![col]! + 1,
        distances[row]![col - 1]! + 1,
        distances[row - 1]![col - 1]! + cost,
      );

      if (
        row > 1 &&
        col > 1 &&
        left[row - 1] === right[col - 2] &&
        left[row - 2] === right[col - 1]
      ) {
        distances[row]![col] = Math.min(distances[row]![col]!, distances[row - 2]![col - 2]! + 1);
      }
    }
  }

  return distances[left.length]![right.length]!;
};

const qwertyNeighbors: Record<string, string> = {
  q: "wa",
  w: "qase",
  e: "wsdr",
  r: "edft",
  t: "rfgy",
  y: "tghu",
  u: "yhji",
  i: "ujko",
  o: "iklp",
  p: "ol",
  a: "qwsz",
  s: "awedxz",
  d: "serfcx",
  f: "drtgvc",
  g: "ftyhbv",
  h: "gyujnb",
  j: "huikmn",
  k: "jiolm",
  l: "kop",
  z: "asx",
  x: "zsdc",
  c: "xdfv",
  v: "cfgb",
  b: "vghn",
  n: "bhjm",
  m: "njk",
};

const qwertyDistance = (left: string, right: string): number => {
  if (left.length !== right.length) {
    return Number.POSITIVE_INFINITY;
  }

  let distance = 0;
  for (let index = 0; index < left.length; index += 1) {
    const from = left[index]!;
    const to = right[index]!;
    if (from === to) {
      continue;
    }
    distance += qwertyNeighbors[from]?.includes(to) ? 1 : 2;
  }
  return distance;
};

const unitDistance = (input: string, candidate: string): number => {
  return Math.min(editDistance(input, candidate), qwertyDistance(input, candidate));
};

const classifyDurationUnit = (unit: string): "hours" | "minutes" | null => {
  const normalizedUnit = unit.toLowerCase();
  const hourUnits = ["h", "hr", "hrs", "hour", "hours"];
  const minuteUnits = ["m", "min", "mins", "minute", "minutes"];

  if (hourUnits.includes(normalizedUnit)) {
    return "hours";
  }
  if (minuteUnits.includes(normalizedUnit)) {
    return "minutes";
  }

  const hourDistance = Math.min(...hourUnits.map(candidate => unitDistance(normalizedUnit, candidate)));
  const minuteDistance = Math.min(...minuteUnits.map(candidate => unitDistance(normalizedUnit, candidate)));
  const allowedDistance = normalizedUnit.length <= 3 ? 1 : 2;

  if (hourDistance <= allowedDistance && hourDistance <= minuteDistance) {
    return "hours";
  }
  if (minuteDistance <= allowedDistance) {
    return "minutes";
  }

  return null;
};

const parseDurationToHours = (value: string): number | null => {
  const normalized = value.trim().toLowerCase();
  if (normalized.length === 0) {
    return 0;
  }

  const bareNumber = Number(normalized);
  if (Number.isFinite(bareNumber)) {
    return bareNumber;
  }

  const clockMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (clockMatch) {
    const hours = Number(clockMatch[1]);
    const minutes = Number(clockMatch[2]);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return hours + minutes / 60;
    }
  }

  let hours = 0;
  let minutes = 0;
  let foundDurationPart = false;
  const pattern = /(\d+(?:\.\d+)?)\s*([a-z]+)/g;

  for (const match of normalized.matchAll(pattern)) {
    const amount = Number(match[1]);
    const unit = classifyDurationUnit(match[2] ?? "");
    if (!Number.isFinite(amount)) {
      continue;
    }
    if (!unit) {
      continue;
    }

    foundDurationPart = true;
    if (unit === "hours") {
      hours += amount;
    } else {
      minutes += amount;
    }
  }

  if (!foundDurationPart) {
    return null;
  }

  return hours + minutes / 60;
};

const snapDurationToHalfHour = (hoursValue: number): number => {
  return Math.min(24, Math.max(0, Math.round(hoursValue * 2) / 2));
};

const clampDuration = (hoursValue: number): number => {
  return Math.min(24, Math.max(0, hoursValue));
};

function DurationField({ id, label, helperText, value, onChange }: DurationFieldProps) {
  const [draftValue, setDraftValue] = useState(value === null ? "" : formatDuration(value));

  useEffect(() => {
    setDraftValue(value === null ? "" : formatDuration(value));
  }, [value]);

  const commit = () => {
    const parsed = parseDurationToHours(draftValue) ?? value ?? 0;
    const next = clampDuration(parsed);
    setDraftValue(formatDuration(next));
    onChange(next);
  };

  const update = (next: number) => {
    const clamped = snapDurationToHalfHour(next);
    setDraftValue(formatDuration(clamped));
    onChange(clamped);
  };

  const currentValue = snapDurationToHalfHour(parseDurationToHours(draftValue) ?? value ?? 0);

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="block text-lg text-[var(--color-text-primary)]">
        {label}
      </label>
      <p className="text-sm text-[var(--color-text-tertiary)]">{helperText}</p>
      <div className="grid grid-cols-[3rem_1fr_3rem] items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label.toLowerCase()}`}
          onClick={() => update(currentValue - 0.5)}
          className="flex h-12 items-center justify-center rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
        >
          <Minus size={18} />
        </button>
        <input
          id={id}
          name={id}
          type="text"
          inputMode="text"
          value={draftValue}
          onChange={event => setDraftValue(event.target.value)}
          onFocus={event => event.currentTarget.select()}
          onBlur={commit}
          className="h-12 w-full rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] px-4 text-center text-xl text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] focus:border-[var(--color-border-focus)]"
        />
        <button
          type="button"
          aria-label={`Increase ${label.toLowerCase()}`}
          onClick={() => update(currentValue + 0.5)}
          className="flex h-12 items-center justify-center rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] text-[var(--color-text-primary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

export function DailyEntryForm({
  dateKey,
  entry,
  symptomOptions,
  transportationOptions,
  appointmentOptions,
  weatherSummary,
  weatherLoading,
  weatherError,
  weatherLocationLabel,
  weatherSourceUrl,
  hasWeatherLocation,
  onUseCurrentLocation,
  onSetCity,
  onSave,
}: DailyEntryFormProps) {
  const [formData, setFormData] = useState<EntryData>(entry);
  const skipNextDebouncedSaveRef = useRef(true);
  const notesDebounceTimerRef = useRef<number | null>(null);
  const latestFormDataRef = useRef(formData);
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (notesDebounceTimerRef.current !== null) {
      window.clearTimeout(notesDebounceTimerRef.current);
      notesDebounceTimerRef.current = null;
    }

    const previousNotes = latestFormDataRef.current.notes;
    setFormData(entry ?? defaultEntry);
    // Skip the debounce only for the programmatic notes change itself.
    skipNextDebouncedSaveRef.current = previousNotes !== (entry?.notes ?? defaultEntry.notes);
  }, [dateKey, entry]);

  useEffect(() => {
    if (skipNextDebouncedSaveRef.current) {
      skipNextDebouncedSaveRef.current = false;
      return;
    }

    if (notesDebounceTimerRef.current !== null) {
      window.clearTimeout(notesDebounceTimerRef.current);
    }

    notesDebounceTimerRef.current = window.setTimeout(() => {
      onSaveRef.current(latestFormDataRef.current);
      notesDebounceTimerRef.current = null;
    }, 800);

    return () => {
      if (notesDebounceTimerRef.current !== null) {
        window.clearTimeout(notesDebounceTimerRef.current);
      }
    };
  }, [formData.notes]);

  const updateField = <K extends keyof EntryData>(field: K, value: EntryData[K]) => {
    const next = { ...formData, [field]: value };
    setFormData(next);
    if (field !== "notes") {
      onSave(next);
    }
  };

  const symptomsToRender = Array.from(new Set([...symptomOptions, ...formData.symptoms]));
  const transportationToRender = Array.from(new Set([...transportationOptions, ...formData.transportation]));
  const appointmentsToRender = Array.from(new Set([...appointmentOptions, ...formData.appointments]));
  const weatherDetails: string[] = [];

  if (formData.weatherCondition) {
    weatherDetails.push(`• Condition: ${formData.weatherCondition}`);
  }
  if (formData.weatherTemperatureMinC !== null || formData.weatherTemperatureMaxC !== null) {
    const min = formatNumber(formData.weatherTemperatureMinC, "°C");
    const max = formatNumber(formData.weatherTemperatureMaxC, "°C");
    if (min && max) {
      weatherDetails.push(`• Temperature: ${min} to ${max}`);
    } else if (min) {
      weatherDetails.push(`• Temperature (min): ${min}`);
    } else if (max) {
      weatherDetails.push(`• Temperature (max): ${max}`);
    }
  }

  const humidity = formatNumber(formData.weatherHumidityAvgPct, "%");
  if (humidity) {
    weatherDetails.push(`• Humidity: ${humidity}`);
  }
  const pressure = formatNumber(formData.weatherPressureMslAvgHpa, " hPa");
  if (pressure) {
    weatherDetails.push(`• Pressure: ${pressure}`);
  }
  const wind = formatNumber(formData.weatherWindMaxKph, " km/h");
  if (wind) {
    weatherDetails.push(`• Wind: ${wind}`);
  }
  const windGust = formatNumber(formData.weatherWindGustMaxKph, " km/h");
  if (windGust) {
    weatherDetails.push(`• Wind gust: ${windGust}`);
  }
  const precipitation = formatNumber(formData.weatherPrecipitationMm, " mm");
  if (precipitation) {
    weatherDetails.push(`• Precipitation: ${precipitation}`);
  }
  const cloudCover = formatNumber(formData.weatherCloudCoverAvgPct, "%");
  if (cloudCover) {
    weatherDetails.push(`• Cloud cover: ${cloudCover}`);
  }
  const visibility = formatNumber(formData.weatherVisibilityAvgM, " m");
  if (visibility) {
    weatherDetails.push(`• Visibility: ${visibility}`);
  }

  const weatherDisplayValue = weatherLoading
    ? "• Status: Fetching weather..."
    : weatherDetails.length > 0
      ? weatherDetails.join("\n")
      : weatherSummary
        ? `• Summary: ${weatherSummary}`
        : "• Status: Not available yet";
  const weatherRows = Math.max(4, Math.min(10, weatherDisplayValue.split("\n").length));

  return (
    <div className="relative z-0 space-y-8 px-4 pb-24 pt-6">
      <div className="space-y-3">
        <p className="block text-lg text-[var(--color-text-primary)]">Did you have a migraine?</p>
        <div className="flex gap-3">
          <button
            onClick={() => updateField("hasMigraine", true)}
            className={`flex-1 rounded-lg h-12 text-base transition-all duration-[var(--transition-base)] border-2 ${
              formData.hasMigraine === true ? chipSelectedClass : chipDefaultClass
            }`}
          >
            Yes
          </button>
          <button
            onClick={() => updateField("hasMigraine", false)}
            className={`flex-1 rounded-lg h-12 text-base transition-all duration-[var(--transition-base)] border-2 ${
              formData.hasMigraine === false ? chipSelectedClass : chipDefaultClass
            }`}
          >
            No
          </button>
        </div>
      </div>

      {formData.hasMigraine === false && (
        <p className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          No migraine recorded for this date. You can still track symptoms, energy, outings, transportation, and notes.
        </p>
      )}

      {formData.hasMigraine === true && (
        <>
          <div className="space-y-3">
            <RangeNumberField
              id="pain-level"
              label="Pain level"
              helperText="0 = no pain, 10 = worst imaginable"
              min={0}
              max={10}
              value={formData.painLevel}
              onChange={next => updateField("painLevel", next)}
            />
          </div>

          <DurationField
            id="migraine-duration-hours"
            label="Migraine duration"
            helperText="How many hours did the migraine last?"
            value={formData.migraineDurationHours}
            onChange={next => updateField("migraineDurationHours", next)}
          />

          <div className="space-y-3">
            <p className="block text-lg text-[var(--color-text-primary)]">Meds taken</p>
            <div className="flex gap-3">
              {[
                ["yes", "Yes"],
                ["partial", "Partial"],
                ["no", "No"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => updateField("medsTaken", value as EntryData["medsTaken"])}
                  className={`flex-1 rounded-lg h-12 text-base transition-all duration-[var(--transition-base)] border-2 ${
                    formData.medsTaken === value ? chipSelectedClass : chipDefaultClass
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {formData.hasMigraine !== null && (
        <>
          <MultiSelectAutogrow
            id={fieldIds.symptoms}
            label="Symptoms"
            helperText="Select all that apply"
            placeholder="Add custom symptom"
            options={toItems(symptomsToRender)}
            value={toItems(formData.symptoms)}
            onChange={next => updateField("symptoms", toUniqueLabels(next))}
            allowCustomValue
            selectedItemsOnTop
          />

          <div className="space-y-3">
            <RangeNumberField
              id="spoons"
              label="Spoons"
              helperText="How much energy did you have today?"
              min={0}
              max={5}
              value={formData.spoons}
              onChange={next => updateField("spoons", next)}
            />
          </div>
        </>
      )}

      {formData.hasMigraine !== null && (
        <>
          <CounterField
            id={fieldIds.outings}
            label="Outings"
            helperText="How many outings did you take today?"
            value={formData.outings}
            onChange={next => updateField("outings", next)}
          />

          <MultiSelectAutogrow
            id={fieldIds.transportation}
            label="Transportation"
            helperText="How did you get around today?"
            placeholder="Add transportation"
            options={toItems(transportationToRender)}
            value={toItems(formData.transportation)}
            onChange={next => updateField("transportation", toUniqueLabels(next))}
            allowCustomValue
            selectedItemsOnTop
          />

          <MultiSelectAutogrow
            id={fieldIds.appointments}
            label="Appointments"
            helperText="What appointments did you have?"
            placeholder="Add appointment"
            options={toItems(appointmentsToRender)}
            value={toItems(formData.appointments)}
            onChange={next => updateField("appointments", toUniqueLabels(next))}
            allowCustomValue
            selectedItemsOnTop
          />

          <div className="space-y-3">
            <label htmlFor={fieldIds.notes} className="block text-lg text-[var(--color-text-primary)]">
              Notes
            </label>
            <p className="text-sm text-[var(--color-text-tertiary)]">Anything else to remember?</p>
            <textarea
              id={fieldIds.notes}
              name={fieldIds.notes}
              value={formData.notes}
              onChange={event => updateField("notes", event.target.value)}
              placeholder="Additional notes, triggers, observations..."
              rows={6}
              className="w-full resize-none rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] transition-colors duration-[var(--transition-base)] focus:border-[var(--color-border-focus)]"
            />
          </div>

        <div className="space-y-3">
          <label htmlFor="daily-weather" className="block text-lg text-[var(--color-text-primary)]">
            Weather
          </label>
          <p className="text-sm text-[var(--color-text-tertiary)]">
            Auto-filled from your location.
            {weatherSourceUrl && (
              <>
                {" "}
                Source:{" "}
                <a
                  href={weatherSourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-[var(--color-border-focus)] underline-offset-2 hover:text-[var(--color-text-secondary)]"
                >
                  Open-Meteo (view this day)
                </a>
              </>
            )}
          </p>
          <textarea
            id="daily-weather"
            name="daily-weather"
            value={weatherDisplayValue}
            readOnly
            rows={weatherRows}
            className="w-full resize-none rounded-lg border-2 border-transparent bg-[var(--color-bg-input)] px-4 py-3 text-[var(--color-text-primary)]"
          />
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            {hasWeatherLocation ? (
              <>
                {weatherLocationLabel && (
                  <span className="rounded bg-[var(--color-bg-elevated)] px-2 py-1 text-[var(--color-text-secondary)]">
                    {weatherLocationLabel}
                  </span>
                )}
                <button
                  type="button"
                  onClick={onSetCity}
                  className="rounded-md border border-[var(--color-border-medium)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
                >
                  Change city
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onUseCurrentLocation}
                  className="rounded-md border border-[var(--color-border-medium)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
                >
                  Use location
                </button>
                <button
                  type="button"
                  onClick={onSetCity}
                  className="rounded-md border border-[var(--color-border-medium)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
                >
                  Set city
                </button>
              </>
            )}
          </div>
          {weatherError && <p className="text-sm text-[var(--color-warning-text)]">{weatherError}</p>}
        </div>
        </>
      )}
    </div>
  );
}
